import { Box, Typography, Paper, Radio, RadioGroup, FormControlLabel, Chip } from '@mui/material';
import { CreditCard as CreditCardIcon, AccountBalance as AccountBalanceIcon, Add as AddIcon } from '@mui/icons-material';
import { UserPaymentMethod } from '@finpay-payment-v3/payment-types';
import { 
  filterCardMethods, 
  filterBankAccounts, 
  formatPaymentMethodDisplay, 
  formatExpiration,
  SavedBankAccount,
} from '../hub-utils';

export interface PaymentMethodListProps {
  savedPaymentMethods: UserPaymentMethod[];
  paymentSource: 'CARD' | 'ACH';
  selectedMethodId: string | null;
  onMethodSelect: (methodId: string) => void;
  title?: string;
  showAddNew?: boolean;
  addNewLabel?: string;
  showDefaultChip?: boolean;
  renderMethodExtra?: (method: UserPaymentMethod) => React.ReactNode;
}

export const PaymentMethodList = ({
  savedPaymentMethods,
  paymentSource,
  selectedMethodId,
  onMethodSelect,
  title,
  showAddNew = true,
  addNewLabel,
  showDefaultChip = true,
  renderMethodExtra,
}: PaymentMethodListProps) => {
  const cardMethods = filterCardMethods(savedPaymentMethods);
  const bankAccounts = filterBankAccounts(savedPaymentMethods);

  const isCard = paymentSource === 'CARD';
  const methods = isCard ? cardMethods : bankAccounts;
  const defaultTitle = isCard ? 'Select Credit/Debit Card' : 'Select Bank Account';
  const defaultAddNewLabel = isCard ? 'Add new credit/debit card' : 'Add new bank account';

  return (
    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa' }} test-id="payment-method-list">
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
        {title || defaultTitle}
      </Typography>
      <RadioGroup
        value={selectedMethodId ?? ''}
        onChange={(e) => onMethodSelect(e.target.value)}
        test-id="payment-method-radiogroup"
      >
        {isCard && cardMethods.map((method: UserPaymentMethod) => {
          const methodId = method.userPaymentMethodId?.toString() || '';
          const isSelected = selectedMethodId === methodId;
          const isDefault = (method as any).isDefault || false;

          return (
            <Paper
              key={methodId}
              variant="outlined"
              test-id={`payment-method-card-${methodId}`}
              sx={{
                p: 1.5,
                mb: 1,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                borderColor: isSelected ? 'primary.main' : 'divider',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => onMethodSelect(methodId)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  value={methodId}
                  control={<Radio size="small" />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCardIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPaymentMethodDisplay(method)}
                          {showDefaultChip && isDefault && (
                            <Chip 
                              label="Default" 
                              size="small" 
                              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                            />
                          )}
                          {renderMethodExtra?.(method)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires {formatExpiration(method)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ flex: 1, m: 0 }}
                />
              </Box>
            </Paper>
          );
        })}

        {!isCard && bankAccounts.map((bankAccount: SavedBankAccount) => {
          const methodId = bankAccount.userPaymentMethodId?.toString() || '';
          const isSelected = selectedMethodId === methodId;
          const isDefault = (bankAccount as any).isDefault || false;

          return (
            <Paper
              key={methodId}
              variant="outlined"
              test-id={`payment-method-ach-${methodId}`}
              sx={{
                p: 1.5,
                mb: 1,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                borderColor: isSelected ? 'primary.main' : 'divider',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() => onMethodSelect(methodId)}
            >
              <FormControlLabel
                value={methodId}
                control={<Radio size="small" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccountBalanceIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">
                      ACH •••• {bankAccount.last4}
                      {showDefaultChip && isDefault && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                        />
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          );
        })}

        {showAddNew && (
          <Paper
            variant="outlined"
            test-id="payment-method-add-new"
            sx={{
              p: 1.5,
              cursor: 'pointer',
              backgroundColor: selectedMethodId === 'new' ? '#e3f2fd' : 'white',
              borderColor: selectedMethodId === 'new' ? 'primary.main' : 'divider',
              borderStyle: 'dashed',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
            onClick={() => onMethodSelect('new')}
          >
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <AddIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary">
                    {addNewLabel || defaultAddNewLabel}
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', m: 0 }}
            />
          </Paper>
        )}
      </RadioGroup>
    </Paper>
  );
};

export default PaymentMethodList;
